import React, { createContext, useContext, useState, useEffect, useCallback } from "react";
import { api } from "../lib/api";

const Ctx = createContext(null);
const KEY = "taven_cj_cart";

// One normalized, unified cart for CJ + Shopify products.
const keyFor = (source, variantId, handle) => `${source}:${variantId || handle}`;

const normalize = (product, qty, source) => {
  const isShopify = source === "shopify";
  // Accept a Shopify variant id for either source (CJ items get one via on-demand mapping).
  const variantId = product.variantId || (isShopify ? (product.defaultVariantId || null) : null);
  if (isShopify && !variantId) return null; // quick-add only when a valid variant exists
  const handle = product.handle;
  return {
    key: keyFor(source, variantId, handle),
    source,
    id: product.taven_product_id || product.handle || product.id || null,
    handle,
    variantId,
    sku: product.sku || product.shopify_sku || null,
    title: product.title,
    image: product.image,
    price: Number(product.price) || 0,
    currency: product.currency || "USD",
    to: isShopify ? `/product/${handle}` : `/market/product/${handle}`,
    quantity: qty,
  };
};

export const CjCartProvider = ({ children }) => {
  const [items, setItems] = useState(() => {
    try {
      const raw = JSON.parse(localStorage.getItem(KEY)) || [];
      return raw.map((x) => x.key ? x : ({
        ...x, source: x.source || "cj",
        key: keyFor(x.source || "cj", x.variantId, x.handle),
        to: x.to || `/market/product/${x.handle}`,
      }));
    } catch { return []; }
  });
  useEffect(() => { localStorage.setItem(KEY, JSON.stringify(items)); }, [items]);

  const add = useCallback((product, qty = 1, source = "cj") => {
    const item = normalize(product, qty, source);
    if (!item) return false;
    setItems((prev) => {
      const i = prev.findIndex((x) => x.key === item.key);
      if (i > -1) { const n = [...prev]; n[i] = { ...n[i], quantity: n[i].quantity + qty }; return n; }
      return [...prev, item];
    });
    return true;
  }, []);

  const remove = useCallback((key) => setItems((prev) => prev.filter((x) => x.key !== key)), []);
  const setQty = useCallback((key, q) => setItems((prev) => prev.map((x) => x.key === key ? { ...x, quantity: Math.max(1, q) } : x)), []);
  const clear = useCallback(() => setItems([]), []);
  const count = items.reduce((s, i) => s + i.quantity, 0);
  const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);

  return <Ctx.Provider value={{ items, add, remove, setQty, clear, count, subtotal }}>{children}</Ctx.Provider>;
};

export const useCjCart = () => useContext(Ctx);
